import React, { useEffect, useState } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, useMediaQuery } from '@mui/material';
import { GetLogByModuleAndRecordId, getLogsBySalesId } from '../../api/logApi';
import { convertDateTimeToDateString } from '../../utils/datetimeUtils';
import MentionInput, { RenderRemarks } from './mentionInput';

// showOnly: 'sales' = only sales field changes, 'commission' = only comm changes, undefined = all
export default function LogViewer({ module, recordId, salesId, remarks, setRemarks, readOnly, hideChanges, showOnly, refreshKey, onSaveRemark }) {
    const [logs, setLogs] = useState([]);
    const [savingRemark, setSavingRemark] = useState(false);

    useEffect(() => {
        if (salesId) {
            getLogsBySalesId(salesId)
                .then(res => setLogs(Array.isArray(res.data) ? res.data : []))
                .catch(() => setLogs([]));
        } else if (module && recordId) {
            GetLogByModuleAndRecordId(module, recordId)
                .then(res => { if (res.status === 200) setLogs(Array.isArray(res.data) ? res.data : []); })
                .catch(() => setLogs([]));
        }
    }, [module, recordId, salesId, refreshKey]);

    const sectionLabel = (text, required) => (
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', mt: 2.5, mb: 1, fontFamily: "'Poppins', sans-serif" }}>
            {text} {required && <span style={{ color: '#EF4444' }}>*</span>}
        </Typography>
    );

    const cellSx = { fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif", padding: '8px 12px', color: '#374151' };
    const headCellSx = { ...cellSx, fontSize: '0.68rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#F8FAFC', borderBottom: '2px solid #E2E8F0' };

    // Filter logs by module if showOnly is set
    const filteredLogs = showOnly === 'sales'
        ? logs.filter(l => l.module === 'Sales')
        : showOnly === 'commission'
            ? logs.filter(l => l.module === 'Commission Progress' || l.module === 'CommissionAmendment')
            : logs;

    const isMobile = useMediaQuery('(max-width:768px)');

    // Parse a log into { changes[], moduleLabel } — shared by the desktop table
    // and the mobile card layout.
    const parseLog = (log) => {
        let changes = [];
        let moduleLabel = log.module === 'Commission Progress' ? 'Comm' : log.module === 'CommissionAmendment' ? 'Amendment' : log.module || '';
        try {
            const oldVal = JSON.parse(log.oldValue || '{}');
            const newVal = JSON.parse(log.newValue || '{}');
            const commType = oldVal?.commissionProgressDto?.commPayType || newVal?.commPayType;
            if (commType && moduleLabel === 'Comm') moduleLabel = commType;
            const oldObj = oldVal?.commissionProgressDto || oldVal?.updateSalesBySalesIdDto || oldVal;
            const newObj = newVal?.updateSalesBySalesIdDto || newVal;
            const commFields = [
                { key: 'commPay', label: 'Commission' },
                { key: 'firstPayment', label: '1st Pay' },
                { key: 'firstPaymentOn', label: '1st Pay Date', isDate: true },
                { key: 'secondPayment', label: '2nd Pay' },
                { key: 'secondPaymentOn', label: '2nd Pay Date', isDate: true },
                { key: 'repayment', label: '3rd Pay' },
                { key: 'repaymentOn', label: '3rd Pay Date', isDate: true },
                { key: 'repayment2', label: '4th Pay' },
                { key: 'repayment2On', label: '4th Pay Date', isDate: true },
                { key: 'repayment3', label: '5th Pay' },
                { key: 'repayment3On', label: '5th Pay Date', isDate: true },
                { key: 'repayment4', label: '6th Pay' },
                { key: 'repayment4On', label: '6th Pay Date', isDate: true },
                { key: 'repayment5', label: '7th Pay' },
                { key: 'repayment5On', label: '7th Pay Date', isDate: true },
                { key: 'paymentStatus', label: 'Pay Status' },
                { key: 'repaymentStatus', label: '3rd Pay Status' },
                { key: 'repayment2Status', label: '4th Pay Status' },
                { key: 'repayment3Status', label: '5th Pay Status' },
                { key: 'repayment4Status', label: '6th Pay Status' },
                { key: 'repayment5Status', label: '7th Pay Status' },
            ];
            const salesFields = [
                { key: 'originalPrice', label: 'Original Price' },
                { key: 'discountByAstern', label: 'Astern Discount' },
                { key: 'discountByAgent', label: 'Agent Discount' },
                { key: 'finalPrice', label: 'Final Price' },
                { key: 'addOnPrice', label: 'Add-On Price' },
                { key: 'panelQty', label: 'Panel Qty' },
                { key: 'energy', label: 'Energy' },
                { key: 'productCategory', label: 'Category' },
                { key: 'paymentMethod', label: 'Payment Method' },
                { key: 'scheme', label: 'Scheme' },
                { key: 'depositOn', label: 'Deposit Date', isDate: true },
                { key: 'customerName', label: 'Customer' },
            ];
            let fields;
            if (showOnly === 'sales') fields = salesFields;
            else if (showOnly === 'commission') fields = commFields;
            else fields = log.module === 'Sales' ? [...salesFields, ...commFields] : commFields;
            const fmt = (v, isDate) => {
                if (v == null || v === '') return '—';
                if (isDate) return convertDateTimeToDateString(v);
                if (typeof v === 'number') return v % 1 === 0 ? String(v) : `RM ${v.toFixed(2)}`;
                return String(v);
            };
            fields.forEach(f => {
                const o = oldObj?.[f.key];
                const n = newObj?.[f.key];
                if (String(o ?? '') !== String(n ?? '')) changes.push(`${f.label}: ${fmt(o, f.isDate)} → ${fmt(n, f.isDate)}`);
            });
        } catch {}
        return { changes, moduleLabel };
    };

    const moduleChip = (moduleLabel) => (salesId && moduleLabel && !showOnly)
        ? <span style={{ display: 'inline-block', padding: '1px 6px', borderRadius: '4px', fontSize: '0.6rem', fontWeight: 600, backgroundColor: '#EFF6FF', color: '#1E40AF' }}>{moduleLabel}</span>
        : null;

    return (
        <Box>
            {!readOnly && (
                <>
                    {sectionLabel('Remarks')}
                    <MentionInput
                        value={remarks}
                        onChange={(val) => setRemarks(val)}
                        placeholder="Enter remarks... Use @ to tag someone"
                        module={module}
                        recordId={recordId || salesId}
                    />
                    {onSaveRemark && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                            <Button
                                size="small"
                                variant="contained"
                                disabled={savingRemark || !remarks?.trim()}
                                onClick={async () => {
                                    setSavingRemark(true);
                                    try { await onSaveRemark(); } finally { setSavingRemark(false); }
                                }}
                                sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none', fontWeight: 600, fontSize: '0.78rem', borderRadius: '8px', boxShadow: 'none' }}
                            >
                                {savingRemark ? 'Sending…' : 'Send'}
                            </Button>
                        </Box>
                    )}
                </>
            )}

            {sectionLabel('Changes History')}
            {!filteredLogs || filteredLogs.length === 0 ? (
                <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', p: 2, textAlign: 'center', color: '#94A3B8', fontSize: '0.8rem', fontFamily: "'Poppins', sans-serif" }}>No changes found.</Box>
            ) : isMobile ? (
                /* Mobile — stacked cards (remarks get full width, no cramped columns) */
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {filteredLogs.map((log) => {
                        const { changes, moduleLabel } = parseLog(log);
                        return (
                            <Box key={log.logId} sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', p: 1.25, fontFamily: "'Poppins', sans-serif" }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.6 }}>
                                    <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                                        {moduleChip(moduleLabel)}
                                        <Typography sx={{ fontSize: '0.7rem', color: '#64748B', fontWeight: 600 }}>{convertDateTimeToDateString(log.createdOn)}</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', whiteSpace: 'nowrap' }}>{log.createdBy}</Typography>
                                </Box>
                                {!hideChanges && changes.length > 0 && (
                                    <Box sx={{ mb: log.remarks ? 0.5 : 0 }}>{changes.map((c, i) => <div key={i} style={{ color: '#1A8CFF', fontSize: '0.72rem' }}>{c}</div>)}</Box>
                                )}
                                {log.remarks
                                    ? <Box sx={{ fontSize: '0.82rem', color: '#374151', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}><RenderRemarks text={log.remarks} /></Box>
                                    : (changes.length === 0 && <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8' }}>{log.action || '—'}</Typography>)}
                            </Box>
                        );
                    })}
                </Box>
            ) : (
                /* Desktop — fixed-layout table so the Remarks wrap instead of overflowing */
                <TableContainer sx={{ borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                    <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                        <TableHead>
                            <TableRow>
                                {!hideChanges && <TableCell sx={{ ...headCellSx, width: '26%' }}>Changes</TableCell>}
                                <TableCell sx={headCellSx}>Remarks</TableCell>
                                <TableCell sx={{ ...headCellSx, width: 96 }}>Date</TableCell>
                                <TableCell sx={{ ...headCellSx, width: 90 }}>By</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredLogs.map((log) => {
                                const { changes, moduleLabel } = parseLog(log);
                                return (
                                    <TableRow key={log.logId} sx={{ '&:hover': { backgroundColor: '#F8FAFC' }, verticalAlign: 'top' }}>
                                        {!hideChanges && (
                                            <TableCell sx={{ ...cellSx, whiteSpace: 'pre-wrap', fontSize: '0.72rem', wordBreak: 'break-word' }}>
                                                {moduleChip(moduleLabel)}
                                                {changes.length > 0 ? changes.map((c, i) => <div key={i} style={{ color: '#1A8CFF' }}>{c}</div>) : <span style={{ color: '#94A3B8' }}>{log.action || '—'}</span>}
                                            </TableCell>
                                        )}
                                        <TableCell sx={{ ...cellSx, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}><RenderRemarks text={log.remarks} /></TableCell>
                                        <TableCell sx={cellSx}>{convertDateTimeToDateString(log.createdOn)}</TableCell>
                                        <TableCell sx={cellSx}>{log.createdBy}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}
